// Les algorithmes de classement de Koinonia.
//
// Ce fichier est VOLONTAIREMENT pur : aucune lecture de base, aucun DOM, aucun
// import. On lui passe des données, il rend un ordre et les raisons de cet ordre.
// C'est ce qui permet de le relire, de le régler et de le tester sans lancer
// l'application — et de le déplacer un jour côté serveur sans rien réécrire.
//
// ─────────────────────────────────────────────────────────────────────────────
// TROIS PARTIS PRIS, ASSUMÉS
//
// 1. La RELATION prime sur la POPULARITÉ. Chez Facebook, un contenu qui marche
//    est poussé parce qu'il marche : c'est la boucle qui fabrique l'indignation,
//    parce que rien n'engage autant que la colère. Ici la popularité compte,
//    mais faiblement (logarithme) et elle ne peut pas dépasser l'affinité.
//
// 2. Une DEMANDE DE PRIÈRE SANS RÉPONSE est remontée, pas enterrée. C'est
//    l'inverse exact d'un classement par engagement : le besoin que personne
//    n'a encore vu passe devant celui qui a déjà reçu cent amens.
//
// 3. Tout est EXPLICABLE. Chaque score porte ses raisons, affichées au membre
//    par « Pourquoi je vois ça ? ». Un classement qu'on ne peut pas expliquer
//    est un classement qu'on ne peut pas corriger.
// ─────────────────────────────────────────────────────────────────────────────

const HEURE = 3600 * 1000;
const JOUR = 24 * HEURE;

/**
 * Tous les réglages en un seul endroit. Modifier une valeur ici change le
 * comportement de l'application entière — c'est fait pour.
 * Voir ALGORITHMES.md pour la justification de chaque nombre.
 */
export const REGLAGES = {
  fil: {
    demiVieHeures: 18,      // au bout de 18 h, une publication pèse moitié moins
    plancherFraicheur: 0.02, // sans plancher, le vieux contenu disparaîtrait tout à fait
    // (plus de coupure d'âge depuis le 20 octobre 2026 : une vieille
    //  publication coule au fond mais reste lisible — voir `scorerPost`)

    // Affinité avec l'auteur — additionnée, puis ramenée à un multiplicateur
    ami: 3,
    demandeEnCours: 0.6,
    parAmiEnCommun: 0.5,    // plafonné, sinon un membre très connecté écrase tout
    plafondAmisEnCommun: 2,
    memeEglise: 2,          // ici l'église pèse plus que la ville : c'est le lien réel
    memeVille: 1,
    espaceCommun: 0.8,      // par groupe ou page partagés
    plafondEspaces: 2.4,
    parAmenDonne: 0.4,      // j'ai déjà dit amen à cette personne
    plafondAmensDonnes: 2,
    parInteractionRecue: 0.5, // cette personne réagit à ce que j'écris
    plafondInteractionsRecues: 2,
    parEchange: 0.5,        // on s'écrit en privé
    plafondEchanges: 2,
    pageSuivie: 1.5,
    moiMeme: 1,

    // Poids du type de publication
    poidsType: { priere: 1.35, temoignage: 1.2, verset: 1, post: 1 },
    poidsImage: 1.05,
    poidsVideo: 1.1,

    // Popularité : bridée par construction
    forceEngagement: 0.25,  // coefficient devant le logarithme
    poidsCommentaire: 2,    // un commentaire vaut deux amens : il coûte plus cher à écrire

    priereSansReponse: 1.4, // le contrepoids : le besoin que personne n'a vu
    dejaVu: 0.35,           // on ne remontre pas en tête ce qui a déjà été lu
    memeAuteur: 0.55,       // ×0.55 par publication du même auteur déjà placée au-dessus
  },

  amis: {
    parAmiEnCommun: 3,      // sous racine carrée : 4 amis communs ne valent pas 4× 1
    memeEglise: 4,          // le signal le plus fort d'un réseau d'église
    memeVille: 2,
    parEspaceCommun: 1.5,
    plafondEspaces: 4.5,
    aReagiChezMoi: 2.5,
    jAiReagiChezLui: 2,
    nouveauMembre: 1.5,     // accueillir celui qui vient d'arriver
    fenetreNouveauJours: 14,
    enLigne: 0.3,           // départage seulement
    scoreMinimum: 1.5,      // en dessous, ce n'est pas une suggestion, c'est du hasard
  },

  contacts: {
    ami: 3,
    demiVieEchangeJours: 7,
    parEchange: 0.6,
    plafondEchanges: 3,
    enLigne: 2,
    amiJamaisContacte: 4,   // « vous êtes amis depuis peu, dites-lui bonjour »
    memeEglise: 1,
    scoreMinimum: 1,
  },

  espaces: {
    parAmiMembre: 2.5,      // sous racine carrée
    memeEglise: 3,
    memeVille: 2,
    forceTaille: 0.5,       // la popularité aide un peu, jamais beaucoup
    scoreMinimum: 1,
  },
};

/* ══════════════════════════════════════════════════════ outils de calcul ══ */

/** Décroissance exponentielle : la moitié du poids perdue à chaque demi-vie. */
function decroissance(ageMs, demiVieMs, plancher = 0) {
  if (ageMs <= 0) return 1;
  return Math.max(plancher, Math.pow(0.5, ageMs / demiVieMs));
}

/** Rendements décroissants : le dixième ami en commun ne vaut pas le premier. */
const racine = (n) => (n > 0 ? Math.sqrt(n) : 0);

/** Plafonne une contribution. Sans plafond, un seul signal finit par tout décider. */
const borner = (valeur, plafond) => Math.min(valeur, plafond);

/** Ajoute une raison au score et la garde pour l'explication. */
function ajouter(bilan, points, cle, texte) {
  if (points <= 0) return;
  bilan.score += points;
  bilan.raisons.push({ cle, texte, points: Math.round(points * 100) / 100 });
}

/** Les trois raisons qui pèsent le plus, en clair. */
export function expliquer(raisons, combien = 3) {
  return [...(raisons || [])]
    .sort((a, b) => b.points - a.points)
    .slice(0, combien)
    .map((r) => r.texte);
}

/**
 * « 3 amis en commun » — la même phrase partout où on la montre.
 *
 * ⚠️ ELLE LIT `amisEnCommun`, ET NON `amisDeMesAmis`. Le second écarte
 * volontairement ceux qui sont déjà mes amis : c'est une carte faite pour
 * SUGGÉRER quelqu'un de nouveau. Or cette phrase s'affiche surtout à côté de
 * gens que je connais déjà — ma liste d'amis, les demandes reçues, leur profil.
 * Voir la note des deux cartes dans `js/signaux.js`.
 *
 * Rend une chaîne VIDE quand il n'y en a aucun : l'appelant écrit alors autre
 * chose, au lieu d'afficher « 0 ami en commun » — une information que personne
 * n'a demandée.
 *
 * @param {object} instantane l'instantané des signaux
 * @param {string} uid        de qui l'on parle
 * @param {boolean} [court]   « 3 en commun », pour les grilles étroites
 */
export function amisEnCommunTexte(instantane, uid, court = false) {
  const n = instantane?.amisEnCommun?.get(uid) || 0;
  if (!n) return "";
  return court ? `${n} en commun` : `${n} ami${n > 1 ? "s" : ""} en commun`;
}

/* ═══════════════════════════════════════════════════ 1. FIL D'ACTUALITÉ ══ */

/**
 * Affinité entre le membre connecté et l'auteur d'une publication.
 * Renvoie { score, raisons }. Un inconnu vaut 0 : il reste visible, mais loin.
 */
export function affinite(post, s) {
  const R = REGLAGES.fil;
  const bilan = { score: 0, raisons: [] };
  const auteur = post.authorId;
  if (!auteur) return bilan;

  if (auteur === s.moi.uid) {
    ajouter(bilan, R.moiMeme, "moi", "Votre publication");
    return bilan;
  }

  const profil = s.membres?.get(auteur);

  if (s.amis.has(auteur)) ajouter(bilan, R.ami, "ami", "Vous êtes amis");
  else if (s.enAttente.has(auteur)) ajouter(bilan, R.demandeEnCours, "attente", "Demande d'amitié en cours");

  const communs = s.amisDeMesAmis.get(auteur) || 0;
  if (communs && !s.amis.has(auteur))
    ajouter(
      bilan,
      borner(communs * R.parAmiEnCommun, R.plafondAmisEnCommun),
      "amis-communs",
      `${communs} ami${communs > 1 ? "s" : ""} en commun`
    );

  if (profil?.eglise && s.moi.eglise && memeTexte(profil.eglise, s.moi.eglise))
    ajouter(bilan, R.memeEglise, "eglise", `Même église : ${profil.eglise}`);
  if (profil?.ville && s.moi.ville && memeTexte(profil.ville, s.moi.ville))
    ajouter(bilan, R.memeVille, "ville", `Habite à ${profil.ville}, comme vous`);

  const espaces = s.espacesCommuns.get(auteur) || 0;
  if (espaces)
    ajouter(
      bilan,
      borner(espaces * R.espaceCommun, R.plafondEspaces),
      "espaces",
      `${espaces} groupe${espaces > 1 ? "s" : ""} ou page${espaces > 1 ? "s" : ""} en commun`
    );

  const donnes = s.auteursSoutenus.get(auteur) || 0;
  if (donnes)
    ajouter(bilan, borner(donnes * R.parAmenDonne, R.plafondAmensDonnes), "mes-amens", "Vous soutenez souvent ses publications");

  const recues = s.interactionsRecues.get(auteur) || 0;
  if (recues)
    ajouter(bilan, borner(recues * R.parInteractionRecue, R.plafondInteractionsRecues), "reactions", "Réagit souvent à ce que vous publiez");

  const echange = s.echanges.get(auteur);
  if (echange?.nombre)
    ajouter(bilan, borner(echange.nombre * R.parEchange, R.plafondEchanges), "messages", "Vous échangez en privé");

  if (post.pageId && s.mesPages.has(post.pageId))
    ajouter(bilan, R.pageSuivie, "page", `Vous suivez ${post.pageNom || "cette page"}`);

  return bilan;
}

/**
 * Score complet d'une publication.
 *
 *      score = (1 + affinité) × poidsDuType × fraîcheur × engagement × malus
 *
 * La forme multiplicative vient d'EdgeRank : chaque facteur peut annuler les
 * autres. Une publication d'un ami très proche mais vieille de trois semaines
 * ne remonte pas ; une publication récente d'un inconnu n'écrase pas un ami.
 */
export function scorerPost(post, s) {
  const R = REGLAGES.fil;
  const age = Math.max(0, s.maintenant - (post.createdAt || 0));
  /* ⚠️ PLUS DE COUPURE D'ÂGE — 20 octobre 2026. `scorerPost` écartait
     (`return null`) toute publication de plus de 21 jours. Dans une petite
     communauté qui ne publie pas tous les jours, le serveur ne renvoie que les
     vingt plus récentes — et si la plupart ont passé trois semaines, elles
     étaient TOUTES jetées : le membre ne voyait plus que ses propres
     publications récentes, les autres n'apparaissant que sur les profils. Josaphat
     l'a vécu comme « les publications ont disparu ». On ne coupe donc plus :
     une vieille publication COULE (la fraîcheur décroît jusqu'au plancher de
     2 %), mais elle reste lisible. C'est le serveur, avec sa limite de vingt
     par page, qui borne le fil — pas une guillotine d'âge. */

  const aff = affinite(post, s);
  const fraicheur = decroissance(age, R.demiVieHeures * HEURE, R.plancherFraicheur);
  const type = R.poidsType[post.type] ?? 1;
  const media = post.video ? R.poidsVideo : post.image ? R.poidsImage : 1;

  const amens = post.amens || 0;
  const commentaires = post.commentaires || 0;
  // Logarithme : passer de 0 à 5 amens compte beaucoup, de 100 à 105 presque rien.
  const engagement = 1 + R.forceEngagement * Math.log1p(amens + R.poidsCommentaire * commentaires);

  // Le contrepoids annoncé en tête de fichier.
  const sansReponse = post.type === "priere" && amens === 0 && commentaires === 0 ? R.priereSansReponse : 1;

  const vu = s.vus.has(post.id) ? R.dejaVu : 1;

  const score = (1 + aff.score) * type * media * fraicheur * engagement * sansReponse * vu;

  const raisons = [...aff.raisons];
  if (type > 1) raisons.push({ cle: "type", texte: libelleType(post.type), points: (type - 1) * 4 });
  if (sansReponse > 1)
    raisons.push({ cle: "sans-reponse", texte: "Personne n'a encore répondu à cette demande de prière", points: 3 });
  if (fraicheur > 0.7) raisons.push({ cle: "recent", texte: "Publié récemment", points: 1.5 });
  if (vu < 1) raisons.push({ cle: "vu", texte: "Vous l'aviez déjà vu", points: 0.1 });

  return { post, score, raisons, details: { affinite: aff.score, fraicheur, type, engagement } };
}

/**
 * Classe un fil complet.
 *
 * `dejaAffiches` fige l'ordre de ce que le membre a déjà sous les yeux : sans
 * cela, chaque nouvelle page chargée réorganiserait l'écran pendant la lecture.
 * Les nouvelles publications sont classées entre elles et ajoutées à la suite —
 * sauf les toutes fraîches, qui remontent en tête comme sur n'importe quel fil.
 */
export function classerFil(posts, s, { dejaAffiches = [] } = {}) {
  const R = REGLAGES.fil;
  const ordreFige = new Set(dejaAffiches);

  const scores = new Map();
  const nouveaux = [];
  // `scorerPost` ne rend plus jamais `null` (plus de coupure d'âge) : toutes
  // les publications reçues sont classées, les vieilles simplement plus bas.
  for (const p of posts) {
    const r = scorerPost(p, s);
    if (!r) continue;
    scores.set(p.id, r);
    if (!ordreFige.has(p.id)) nouveaux.push(r);
  }

  // Une publication arrivée depuis l'ouverture de l'écran passe devant : c'est
  // ce que le membre attend quand il voit « nouvelle publication ».
  const recemment = (r) => s.maintenant - r.post.createdAt < 2 * HEURE;
  const fraiches = ordreFige.size ? nouveaux.filter(recemment) : [];
  const reste = ordreFige.size ? nouveaux.filter((r) => !recemment(r)) : nouveaux;

  // À score égal, le plus récent d'abord — sans quoi l'ordre d'insertion
  // déciderait, et un fil entièrement ancien (fraîcheur au plancher pour tous)
  // s'afficherait dans un ordre arbitraire.
  const parScore = (a, b) => b.score - a.score || (b.post.createdAt || 0) - (a.post.createdAt || 0);
  // La tête est intouchable : ce que le membre est en train de lire ne doit pas
  // se réorganiser sous ses yeux parce qu'une page est arrivée.
  const tete = [...fraiches.sort(parScore), ...dejaAffiches.map((id) => scores.get(id)).filter(Boolean)];

  // La diversité tient compte des auteurs déjà présents en tête.
  const dejaPlaces = new Map();
  for (const r of tete) dejaPlaces.set(r.post.authorId, (dejaPlaces.get(r.post.authorId) || 0) + 1);

  return [...tete, ...diversifier(reste.sort(parScore), dejaPlaces, R.memeAuteur)];
}

/**
 * Évite le mur d'un seul auteur. À chaque tour on prend la meilleure publication
 * restante APRÈS pénalité, puis on incrémente le compte de son auteur : celui qui
 * publie dix fois de suite voit chacune de ses publications valoir un peu moins
 * que la précédente. Sans cela, il occuperait l'écran à lui seul.
 *
 * Choix glouton, en n² sur une page de fil : quelques milliers d'opérations.
 */
function diversifier(liste, compteInitial, facteur) {
  const compte = new Map(compteInitial);
  const restants = [...liste];
  const sortie = [];
  while (restants.length) {
    let choisi = 0;
    let meilleur = -Infinity;
    for (let i = 0; i < restants.length; i++) {
      const r = restants[i];
      const penalise = r.score * Math.pow(facteur, compte.get(r.post.authorId) || 0);
      if (penalise > meilleur) {
        meilleur = penalise;
        choisi = i;
      }
    }
    const [r] = restants.splice(choisi, 1);
    compte.set(r.post.authorId, (compte.get(r.post.authorId) || 0) + 1);
    sortie.push(meilleur === r.score ? r : { ...r, score: meilleur });
  }
  return sortie;
}

/* ══════════════════════════════════════════════ 2. SUGGESTIONS D'AMIS ══ */

/**
 * « Vous connaissez peut-être ». Le cœur est le nombre d'amis en commun, sous
 * racine carrée : c'est ce que fait Facebook, et pour une bonne raison — le
 * premier ami commun est une information, le vingtième n'en est plus une.
 */
export function suggererAmis(candidats, s, { combien = 12 } = {}) {
  const R = REGLAGES.amis;
  const resultats = [];

  for (const u of candidats) {
    if (!u?.uid || u.uid === s.moi.uid) continue;
    if (s.amis.has(u.uid) || s.enAttente.has(u.uid)) continue;
    if (s.bloques.has(u.uid) || s.ignores.has(u.uid)) continue;
    if (u.validation === "en_attente" || u.validation === "refuse") continue;

    const bilan = { score: 0, raisons: [] };
    const communs = s.amisDeMesAmis.get(u.uid) || 0;
    if (communs)
      ajouter(bilan, racine(communs) * R.parAmiEnCommun, "amis-communs", `${communs} ami${communs > 1 ? "s" : ""} en commun`);

    if (u.eglise && s.moi.eglise && memeTexte(u.eglise, s.moi.eglise))
      ajouter(bilan, R.memeEglise, "eglise", `Même église : ${u.eglise}`);
    if (u.ville && s.moi.ville && memeTexte(u.ville, s.moi.ville))
      ajouter(bilan, R.memeVille, "ville", `Habite à ${u.ville}`);

    const espaces = s.espacesCommuns.get(u.uid) || 0;
    if (espaces)
      ajouter(bilan, borner(espaces * R.parEspaceCommun, R.plafondEspaces), "espaces", `${espaces} groupe${espaces > 1 ? "s" : ""} ou page${espaces > 1 ? "s" : ""} en commun`);

    if (s.interactionsRecues.get(u.uid)) ajouter(bilan, R.aReagiChezMoi, "reactions", "A réagi à vos publications");
    if (s.auteursSoutenus.get(u.uid)) ajouter(bilan, R.jAiReagiChezLui, "mes-amens", "Vous avez réagi à ses publications");

    if (u.createdAt && s.maintenant - u.createdAt < R.fenetreNouveauJours * JOUR)
      ajouter(bilan, R.nouveauMembre, "nouveau", "Vient d'arriver sur Koinonia");

    if (s.enLigne.has(u.uid)) ajouter(bilan, R.enLigne, "enligne", "En ligne");

    if (bilan.score >= R.scoreMinimum) resultats.push({ membre: u, score: bilan.score, raisons: bilan.raisons });
  }

  return resultats.sort((a, b) => b.score - a.score).slice(0, combien);
}

/* ═══════════════════════════════════════════ 3. SUGGESTIONS DE CONTACTS ══ */

/**
 * « À qui écrire ». Trois idées : ceux avec qui l'échange est vivant, ceux qui
 * sont connectés là maintenant, et les amitiés récentes jamais suivies d'un mot.
 */
export function suggererContacts(candidats, s, { combien = 8 } = {}) {
  const R = REGLAGES.contacts;
  const resultats = [];

  for (const u of candidats) {
    if (!u?.uid || u.uid === s.moi.uid || s.bloques.has(u.uid)) continue;

    const bilan = { score: 0, raisons: [] };
    const ami = s.amis.has(u.uid);
    if (ami) ajouter(bilan, R.ami, "ami", "Vous êtes amis");

    const e = s.echanges.get(u.uid);
    if (e?.nombre) {
      ajouter(bilan, borner(e.nombre * R.parEchange, R.plafondEchanges), "echanges", "Vous vous écrivez régulièrement");
      // une conversation vieille de trois semaines n'est plus une suggestion
      const recence = decroissance(s.maintenant - (e.dernier || 0), R.demiVieEchangeJours * JOUR);
      bilan.score *= 0.4 + 0.6 * recence;
    } else if (ami) {
      ajouter(bilan, R.amiJamaisContacte, "jamais", "Vous ne vous êtes jamais écrit");
    }

    if (s.enLigne.has(u.uid)) ajouter(bilan, R.enLigne, "enligne", "En ligne maintenant");
    if (!ami && u.eglise && s.moi.eglise && memeTexte(u.eglise, s.moi.eglise))
      ajouter(bilan, R.memeEglise, "eglise", "Même église");

    if (bilan.score >= R.scoreMinimum) resultats.push({ membre: u, score: bilan.score, raisons: bilan.raisons });
  }

  return resultats.sort((a, b) => b.score - a.score).slice(0, combien);
}

/* ═══════════════════════════════ 4. SUGGESTIONS DE GROUPES ET DE PAGES ══ */

/**
 * Un groupe ou une page se recommande d'abord par les amis qui y sont déjà,
 * ensuite par l'église et la ville qu'il nomme, et très peu par sa taille.
 * `membresDe` donne, pour un identifiant d'espace, l'ensemble de ses membres.
 */
export function suggererEspaces(espaces, s, { dejaDedans = new Set(), membresDe = new Map(), combien = 6 } = {}) {
  const R = REGLAGES.espaces;
  const resultats = [];

  for (const g of espaces) {
    if (!g?.id || dejaDedans.has(g.id)) continue;

    const membres = membresDe.get(g.id) || new Set();
    const amisDedans = [...s.amis].filter((uid) => membres.has(uid));

    const bilan = { score: 0, raisons: [] };
    if (amisDedans.length)
      ajouter(bilan, racine(amisDedans.length) * R.parAmiMembre, "amis", `${amisDedans.length} de vos amis en ${amisDedans.length > 1 ? "font" : "fait"} partie`);

    const texte = `${g.nom || ""} ${g.description || ""} ${g.eglise || ""} ${g.ville || ""}`;
    if (s.moi.eglise && contient(texte, s.moi.eglise)) ajouter(bilan, R.memeEglise, "eglise", `Lié à ${s.moi.eglise}`);
    if (s.moi.ville && contient(texte, s.moi.ville)) ajouter(bilan, R.memeVille, "ville", `À ${s.moi.ville}`);

    const taille = membres.size || g.nbMembres || g.nbAbonnes || 0;
    if (taille) ajouter(bilan, R.forceTaille * Math.log1p(taille), "taille", `${taille} membre${taille > 1 ? "s" : ""}`);

    if (bilan.score >= R.scoreMinimum) resultats.push({ espace: g, score: bilan.score, raisons: bilan.raisons });
  }

  return resultats.sort((a, b) => b.score - a.score).slice(0, combien);
}

/* ════════════════════════════════════════════════ 5. RECHERCHE CLASSÉE ══ */

/**
 * La recherche ne rend pas l'ordre alphabétique : un ami qui correspond passe
 * avant un inconnu qui correspond, et un nom qui COMMENCE par la requête passe
 * avant un nom qui la contient au milieu.
 */
export function classerRecherche(membres, requete, s) {
  const q = normaliser(requete);
  if (!q) return membres;

  return membres
    .map((u) => {
      const nom = normaliser(u.nom);
      let score = 0;
      if (nom === q) score += 10;
      else if (nom.startsWith(q)) score += 6;
      else if (nom.split(/\s+/).some((mot) => mot.startsWith(q))) score += 4;
      else if (nom.includes(q)) score += 2;
      if (normaliser(u.eglise || "").includes(q)) score += 1.5;
      if (normaliser(u.ville || "").includes(q)) score += 1;

      if (s.amis.has(u.uid)) score += 3;
      else if (s.amisDeMesAmis.get(u.uid)) score += Math.min(2, racine(s.amisDeMesAmis.get(u.uid)));
      if (s.moi.eglise && u.eglise && memeTexte(u.eglise, s.moi.eglise)) score += 1;
      if (s.enLigne.has(u.uid)) score += 0.3;
      return { u, score };
    })
    .sort((a, b) => b.score - a.score || String(a.u.nom || "").localeCompare(String(b.u.nom || "")))
    .map((x) => x.u);
}

/* ═══════════════════════════════════════════════════════════ chaînes ══ */

/** Comparaison tolérante : accents, casse et espaces ne doivent pas séparer. */
export const normaliser = (s) =>
  (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/\s+/g, " ").trim();

const memeTexte = (a, b) => Boolean(a) && normaliser(a) === normaliser(b);
const contient = (texte, aiguille) => {
  const n = normaliser(aiguille);
  return n.length > 2 && normaliser(texte).includes(n);
};

const libelleType = (t) =>
  t === "priere" ? "Demande de prière" : t === "temoignage" ? "Témoignage" : t === "verset" ? "Verset" : "Publication";
